/**
 * Company alerts in the account menu (ADR-022):
 *
 *   - Company seat invitations: someone offered the viewer an owner or admin
 *     seat. Nothing is held until they accept, which needs MFA like every
 *     Company route; Decline answers it for good.
 *   - Link notices: a workspace the viewer belongs to joined a Company, so
 *     arches that Company approves can now read what the viewer can see
 *     there. Told to every member, not only the owner who accepted;
 *     dismissed per viewer.
 *
 * Showing the invitations marks them seen (the header dot clears); notices
 * count until dismissed. Renders nothing when there is nothing to say.
 */

import { useEffect } from 'react';
import { Building2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import type { CompanySeatInvitationForMeDto } from '@/lib/api';
import { useAuth } from '../hooks/useAuth';
import {
  useAcceptCompanySeatInvitation,
  useDeclineCompanySeatInvitation,
  useMyCompanySeatInvitations,
} from '../hooks/useCompanies';
import {
  dismissCompanyLinkNotice,
  markInvitationsSeen,
  useCompanyLinkNotices,
} from '../hooks/useInvitationAlerts';
import { describeCompanyError } from '../lib/company-errors';

function inviterLabel(inv: CompanySeatInvitationForMeDto): string {
  const by = inv.invitedBy;
  return by.displayName ?? (by.username ? `@${by.username}` : 'Someone');
}

/** Accept / Decline for each open seat invitation. Shared by the menu and the Company page. */
export function CompanySeatInvitationsList({ compact = false }: { compact?: boolean }) {
  const { isAuthenticated, isGuest } = useAuth();
  const query = useMyCompanySeatInvitations({ enabled: isAuthenticated && !isGuest });
  const accept = useAcceptCompanySeatInvitation();
  const decline = useDeclineCompanySeatInvitation();
  const list = query.data ?? [];

  useEffect(() => {
    const ids = (query.data ?? []).map((i) => i.invitationId);
    if (ids.length > 0) markInvitationsSeen(ids);
  }, [query.data]);

  if (list.length === 0) return null;

  async function handleAccept(inv: CompanySeatInvitationForMeDto): Promise<void> {
    try {
      await accept.mutateAsync(inv.invitationId);
      toast({
        title: `You are now ${inv.role === 'owner' ? 'an owner' : 'an admin'} of ${inv.companyName}`,
      });
    } catch (err) {
      toast({
        title: 'Could not accept',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  async function handleDecline(inv: CompanySeatInvitationForMeDto): Promise<void> {
    try {
      await decline.mutateAsync(inv.invitationId);
      toast({ title: `Declined ${inv.companyName}’s invitation` });
    } catch (err) {
      toast({
        title: 'Could not decline',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <ul className="space-y-2">
      {list.map((inv) => (
        <li
          key={inv.invitationId}
          className={
            compact
              ? 'space-y-1.5 text-xs'
              : 'flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm'
          }
        >
          <p className="text-foreground">
            {inviterLabel(inv)} invited you to be {inv.role === 'owner' ? 'an owner' : 'an admin'}{' '}
            of <span className="font-medium">{inv.companyName}</span>. Company roles manage billing,
            links and approvals; they give no access to any workspace&apos;s data.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className={compact ? 'h-7 px-2 text-xs' : undefined}
              onClick={() => void handleAccept(inv)}
              disabled={accept.isPending || decline.isPending}
              aria-label={`Accept ${inv.companyName}’s invitation`}
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={compact ? 'h-7 px-2 text-xs' : undefined}
              onClick={() => void handleDecline(inv)}
              disabled={accept.isPending || decline.isPending}
              aria-label={`Decline ${inv.companyName}’s invitation`}
            >
              Decline
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** "<workspace> joined <Company>" notices, each dismissible by this viewer. */
export function CompanyLinkNoticesList() {
  const notices = useCompanyLinkNotices();
  if (notices.length === 0) return null;
  return (
    <ul className="space-y-2">
      {notices.map((n) => (
        <li key={n.invitationId} className="flex items-start gap-2 text-xs">
          <p className="flex-1 text-foreground">
            <span className="font-medium">{n.holderDisplayName}</span> joined{' '}
            <span className="font-medium">{n.companyName}</span>. Arches the Company approves can
            now read what you can see there.
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 shrink-0 p-0"
            onClick={() => dismissCompanyLinkNotice(n.invitationId)}
            aria-label={`Dismiss: ${n.holderDisplayName} joined ${n.companyName}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** The account menu's Company alerts block; nothing when there is nothing to say. */
export function AccountAlerts() {
  const { isAuthenticated, isGuest } = useAuth();
  const seatInvitations = useMyCompanySeatInvitations({ enabled: isAuthenticated && !isGuest });
  const notices = useCompanyLinkNotices();
  if ((seatInvitations.data?.length ?? 0) === 0 && notices.length === 0) return null;
  return (
    <section
      aria-label="Company alerts"
      className="space-y-2 border-b border-border bg-primary/5 p-3"
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Building2 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        Company
      </p>
      <CompanySeatInvitationsList compact />
      <CompanyLinkNoticesList />
    </section>
  );
}
